/**
 * Conteúdo interno do modal para envio ao prontuário (sem Dialog próprio).
 * Lista as 8 métricas do dashboard; sem valor = checkbox desmarcado e desabilitado.
 */

import { Add } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useConsentContext } from "@/contexts/ConsentContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePrivacyContext } from "@/contexts/PrivacyContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetConsents } from "@/services/consent";
import { PostWearableObservationResource } from "@/services/fhir/observation/PostWearableObservation";
import {
  WEARABLE_LOINC_LIST,
  buildWearableObservations,
  hasWearableValueForKey,
  type WearableShareKey,
} from "@/services/fhir/observation/wearableObservationBuilder";
import { useDatetime } from "@/utils/useDatetime";
import { useFormatter } from "@/utils/useFormatter";
import type { IGoogleHealthFitnessBundle } from "@/services/google/health";
import { formatarDadosGoogleHealth } from "@/utils/wearableFitFormat";
import { ConsentTabs } from "@/pages/consents/components/ConsentTabs";
import { ConfirmConsentDialog } from "@/pages/consents/components/ConfirmConsentDialog";
import { RenewConsentDialog } from "@/pages/consents/components/RenewConsentDialog";
import { RevokeConsentDialog } from "@/pages/consents/components/RevokeConsentDialog";

import type { Dayjs } from "dayjs";

interface FitnessDataSharePanelProps {
  fitnessBundle: IGoogleHealthFitnessBundle | null;
  selectedDate: Dayjs;
  /** Volta à tela de cards / busca. */
  onBack: () => void;
  /** Fecha o modal de fitness ao sair para outra rota (ex.: /consent). */
  onCloseModal?: () => void;
}

export function FitnessDataSharePanel({
  fitnessBundle,
  selectedDate,
  onBack,
  onCloseModal,
}: FitnessDataSharePanelProps) {
  const navigate = useNavigate();
  const { user } = useSessionContext();
  const { isSharing } = usePrivacyContext();
  const { organizationList } = useOrganizationContext();
  const {
    confirmConsentDialog,
    revokeDialog,
    renewDialog,
    setConsentType,
  } = useConsentContext();

  const { formatNameDisplay } = useFormatter;
  const { formatToDDMMYYYY, formatToHHmm } = useDatetime;

  const [searchField] = useState("");
  const [granted, setGranted] = useState<any>([]);
  const [received] = useState([]);
  const [refetch, setRefetch] = useState(false);
  const [selectedTab, setSelectedTab] = useState("1");
  const [sending, setSending] = useState(false);

  const fit = useMemo(
    () => formatarDadosGoogleHealth(fitnessBundle, selectedDate),
    [fitnessBundle, selectedDate]
  );

  const [selectedKeys, setSelectedKeys] = useState<Record<string, boolean>>({});

  // Ao mudar os dados, marca só o que tem valor; sem valor fica false e desabilitado na UI.
  useEffect(() => {
    const initial: Record<string, boolean> = {};
    for (const def of WEARABLE_LOINC_LIST) {
      initial[def.key] = hasWearableValueForKey(fit, def.key);
    }
    setSelectedKeys(initial);
    setSelectedTab("1");
  }, [fit]);

  // Não chamar resetStates() aqui: a função do contexto não é estável (novo identity a cada
  // render do provider), então um efeito com [resetStates] reexecutava e fechava Renovar/Revogar
  // logo após abrir.

  const formatConsent = useCallback(
    (identifier: any, name: any, el: any) => {
      const isValid =
        dayjs(el?.vigencia?.split("T")?.[0]).isAfter(dayjs()) || !el?.vigencia;

      return {
        identifier: identifier || "-",
        name: formatNameDisplay(name || identifier) || "-",
        date: dayjs(el?.hora).isValid() ? formatToDDMMYYYY(dayjs(el.hora)) : "-",
        hour: dayjs(el?.hora).isValid() ? formatToHHmm(dayjs(el.hora)) : "-",
        vigency: dayjs(el?.vigencia).isValid()
          ? formatToDDMMYYYY(dayjs(el.vigencia))
          : null,
        isValid,
        motivo: el?.contexto?.motivoAcesso,
        status: isValid ? "valid" : "expired",
      };
    },
    [formatNameDisplay, formatToDDMMYYYY, formatToHHmm]
  );

  useEffect(() => {
    if (!isSharing) return;

    async function fetchData() {
      try {
        const grantedResponse = await GetConsents({
          identifier: user.username,
          type: "granted",
        });

        const activeConsents = grantedResponse?.data?.filter(
          (el: any) => el?.acao === "Autorizar"
        );

        const auxIndividual =
          activeConsents
            ?.filter(
              (el: any) =>
                !el.contexto.instituicao &&
                el?.sujeito?.id !== user.username &&
                el?.sujeito?.nome
            )
            ?.map((el: any) =>
              formatConsent(el?.sujeito?.id, el?.sujeito?.nome, el)
            ) || [];

        const auxOrganization =
          activeConsents
            ?.filter((el: any) => el.contexto.instituicao)
            ?.map((el: any) => {
              const organization = organizationList?.find(
                (org: any) =>
                  org.resource?.id.split("-")[1] === el?.contexto?.instituicao
              );
              const alias =
                organization?.resource?.alias?.[0] !== "null"
                  ? organization?.resource?.alias?.[0]
                  : organization?.resource?.name;
              return formatConsent(el?.contexto?.instituicao, alias, el);
            }) || [];

        setGranted([auxIndividual, auxOrganization]);
      } catch (err) {
        console.error("Fetch consents (wearable share)", err);
      }
    }

    fetchData();
  }, [isSharing, user.username, refetch, organizationList, formatConsent]);

  const handleChangeTab = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    if (newValue === "1") setConsentType("cpf");
    else if (newValue === "2") setConsentType("cnpj");
  };

  const selectableCount = useMemo(() => {
    return WEARABLE_LOINC_LIST.filter(
      (d) =>
        hasWearableValueForKey(fit, d.key) && selectedKeys[d.key]
    ).length;
  }, [fit, selectedKeys]);

  const handleSubmit = async () => {
    const keys = (Object.keys(selectedKeys) as WearableShareKey[]).filter(
      (k) => selectedKeys[k] && hasWearableValueForKey(fit, k)
    );
    if (!keys.length) {
      toast.error("Selecione ao menos um dado com valor para enviar.");
      return;
    }

    const observations = buildWearableObservations({
      fit,
      selectedDate,
      patientId: user.username,
      patientDisplay: user.name || user.username,
      keys,
    });

    if (!observations.length) {
      toast.error("Nenhuma observação válida para enviar.");
      return;
    }

    setSending(true);
    try {
      for (const obs of observations) {
        await PostWearableObservationResource(obs);
      }
      toast.success(
        `${observations.length} observação(ões) enviada(s) com sucesso.`
      );
      onBack();
    } catch {
      /* toast já em PostWearableObservation */
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Stack spacing={1.25} sx={{ pt: 0.5 }}>
        {!isSharing ? (
          <Typography variant="body2" color="text.secondary">
            Para registrar observações no prontuário, é necessário permitir
            compartilhamento nas configurações de privacidade.{" "}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate("/privacy")}
              sx={{ verticalAlign: "baseline" }}
            >
              Abrir privacidade
            </Link>
          </Typography>
        ) : (
          <>
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={700} color="primary800.main">
                Quem poderá acessar o que você enviar
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                As <strong>pessoas</strong> e as <strong>unidades de saúde</strong> com
                compartilhamento <strong>concedido</strong> por você (listados nas abas
                abaixo) poderão <strong>visualizar no prontuário</strong> as informações
                de saúde que você registrar ao confirmar o envio.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => {
                  navigate("/consent");
                  onCloseModal?.();
                }}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                Novo compartilhamento
              </Button>

              <ConsentTabs
                embedGrantedOnly
                selectedTab={selectedTab}
                handleChangeTab={handleChangeTab}
                searchField={searchField}
                granted={granted}
                received={received}
              />
            </Stack>

            <Typography variant="subtitle1" fontWeight={700} color="primary800.main" sx={{ mt: 1 }}>
              O que deseja enviar
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
              Marque as medições do dia{" "}
              <strong>{selectedDate.format("DD/MM/YYYY")}</strong>. Itens sem dados neste
              dia ficam desabilitados para o envio ao prontuário.
            </Typography>

            <FormGroup
              sx={{
                mt: 0.25,
                gap: 0,
                "& .MuiFormControlLabel-root": {
                  margin: 0,
                  minHeight: 0,
                  py: 0.125,
                },
              }}
            >
              {WEARABLE_LOINC_LIST.map((def) => {
                const hasValue = hasWearableValueForKey(fit, def.key);
                return (
                  <FormControlLabel
                    key={def.key}
                    sx={{
                      alignItems: "center",
                      opacity: hasValue ? 1 : 0.55,
                      m: 0,
                      py: 0,
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={hasValue && Boolean(selectedKeys[def.key])}
                        disabled={!hasValue}
                        onChange={(e) => {
                          if (!hasValue) return;
                          setSelectedKeys((prev) => ({
                            ...prev,
                            [def.key]: e.target.checked,
                          }));
                        }}
                        sx={{ py: 0 }}
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                        {def.text}
                      </Typography>
                    }
                  />
                );
              })}
            </FormGroup>

            <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={sending || selectableCount === 0}
                sx={{ textTransform: "none" }}
              >
                {sending ? "Enviando..." : "Enviar selecionados"}
              </Button>
            </Stack>
          </>
        )}
      </Stack>

      {confirmConsentDialog && (
        <ConfirmConsentDialog setRefetch={setRefetch} />
      )}
      {revokeDialog && <RevokeConsentDialog setRefetch={setRefetch} />}
      {renewDialog && <RenewConsentDialog setRefetch={setRefetch} />}
    </>
  );
}
